import React, { useState } from "react";

import {
  fetchWithToken,
  getUserDetails,
  redirectToLogout,
  useAlternateLayout,
} from "@platform/core";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  PlatformBase,
  pxToRem,
  setTheme,
  Toolbar,
  Typography,
} from "@platform/style";

import UserMenu from "./UserMenu";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretDown } from "@fortawesome/sharp-solid-svg-icons";

import {
  faArrowUpRightFromSquare,
  faGear,
  faInfoCircle,
  faRightFromBracket,
} from "@fortawesome/sharp-regular-svg-icons";
import { HEADERBFF_URL } from "../constants";
import useOneAdmin from "./hooks/useOneAdmin";
import { addChatResponse } from "@platform/chatwindow-utils";
import { HeaderButtons } from "./HeaderButtons";
import { NavMenuToggleButton } from "./NavMenuToggleButton";
import { About } from "./About";
import Logo from "../static/logo.svg";

interface Preferences {
  CompanyId: string;
  Id: string;
  Theme: string;
  UserId: string;
}

library.add(faCaretDown, faGear, faRightFromBracket, faArrowUpRightFromSquare, faInfoCircle);

export default function AppHeader() {
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [userMenuDialogOpen, setUserMenuDialogOpen] = React.useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [preferencesId, setPreferencesId] = React.useState("");
  const [isErrorGettingOALink, setIsErrorGettingOALink] = React.useState<boolean>(false);
  const { isAdmin, linkToOneAdmin } = useOneAdmin();
  const alternateLayout = useAlternateLayout();

  React.useEffect(() => {
    if (isErrorGettingOALink) {
      addChatResponse("platform-header-chatContent-backendFailureAlertResponse");
    }
  }, [isErrorGettingOALink]);

  const open = Boolean(userMenuAnchor);

  const userFullName = getUserDetails().fullName;
  const userInitials = userFullName
    ?.split(" ")
    .map((segment) => segment.charAt(0))
    .join("");

  const handleUserMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleUserMenuDialogClickOpen = () => {
    setUserMenuDialogOpen(true);
    handleUserMenuClose();
    window.dispatchEvent(new Event("openAccountSettings"));
  };

  const handleUserMenuDialogClose = () => {
    setUserMenuDialogOpen(false);
    window.dispatchEvent(new Event("closeAccountSettings"));
  };

  const handleAboutOpen = () => {
    setAboutOpen(true);
    handleUserMenuClose();
  };

  const handleAboutClose = () => {
    setAboutOpen(false);
  };

  React.useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, []);

  const fetchData = async () => {
    const options: RequestInit = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    };
    let response;
    try {
      response = await fetchWithToken(`${HEADERBFF_URL}/v1/preferences`, options);
    } catch (e) {
      return;
    }
    const decoded = (await response.json()) as Preferences;
    if (decoded.Theme === "Light" || decoded.Theme === "Dark" || decoded.Theme === "Auto") {
      if (localStorage.getItem("theme") !== decoded.Theme) {
        setTheme(decoded.Theme);
      }
    } else {
      setTheme("Light");
    }
    setPreferencesId(decoded.Id);
  };

  return (
    <PlatformBase>
      <AppBar sx={{ zIndex: "1400" }}>
        <Toolbar
          disableGutters={true}
          sx={{
            backgroundColor: "header.main",
            height: "100%",
            paddingRight: pxToRem(16),
            paddingLeft: "1.5rem",
          }}
        >
          {alternateLayout && <NavMenuToggleButton />}
          <Box sx={{ display: "flex", alignItems: "center", flex: "1 1 auto", mb: 1 }}>
            <img
              src={Logo}
              alt="MSP® DX Logo"
              style={{ height: "32px", marginRight: "8px", marginTop: "8px" }}
            />
          </Box>
          {alternateLayout && <HeaderButtons />}
          <Avatar data-testid={"Avatar"} alt={userFullName} sx={{ fontWeight: "bold" }}>
            {userInitials}
          </Avatar>
          <Typography
            variant={"body1"}
            noWrap
            sx={{ display: { xs: "none", md: "inline-flex" }, color: "header.contrastText" }}
          >
            {userFullName}
          </Typography>
          <IconButton
            id="user-button"
            name={"user-button"}
            aria-label="user menu button"
            sx={{
              color: "header.contrastText",
              paddingLeft: "12px",
              paddingRight: "12px",
            }}
            aria-controls={open ? "user-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleUserMenuClick}
          >
            <FontAwesomeIcon icon={faCaretDown} fontSize={"14px"} data-testid={"CaretDownIcon"} />
          </IconButton>
          <Menu
            id="user-menu"
            anchorEl={userMenuAnchor}
            open={open}
            onClose={handleUserMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            MenuListProps={{
              "aria-labelledby": "user-button",
            }}
            sx={{
              zIndex: "1450",
              width: pxToRem(230),
            }}
          >
            <MenuItem
              onClick={() => handleUserMenuDialogClickOpen()}
              sx={{ minWidth: pxToRem(206) }}
            >
              <ListItemIcon>
                <FontAwesomeIcon icon={faGear} />
              </ListItemIcon>
              <ListItemText>Account Settings</ListItemText>
            </MenuItem>
            {isAdmin && (
              <MenuItem
                onClick={() => {
                  handleUserMenuClose();
                  linkToOneAdmin()
                    .then(() => setIsErrorGettingOALink(false))
                    .catch(() => setIsErrorGettingOALink(true));
                }}
                sx={{ minWidth: pxToRem(206) }}
              >
                <ListItemIcon>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </ListItemIcon>
                <ListItemText>One Admin</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleAboutOpen}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faInfoCircle} data-testid="info-icon" />
              </ListItemIcon>
              <ListItemText>About</ListItemText>
            </MenuItem>
            <MenuItem onClick={redirectToLogout}>
              <ListItemIcon>
                <FontAwesomeIcon icon={faRightFromBracket} />
              </ListItemIcon>
              <ListItemText>Log out</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <UserMenu
        open={userMenuDialogOpen}
        handleClose={handleUserMenuDialogClose}
        preferencesId={preferencesId}
      />
      <About open={aboutOpen} onClose={handleAboutClose} />
    </PlatformBase>
  );
}
